/**
 * Discover Tab Content Component
 * Renders the discover tab content for a response pair
 */

import React, { useState, useEffect } from 'react';
import { ChatMessage, ChatLocation } from '@/types/chatTypes';
import {
  PeopleAlsoAsk as PeopleAlsoAskType,
  ExperienceCard,
} from '@/constants/chatScreenData';
import { spacing, strokeColors } from '@/design-system/utils';
import { OverviewSection } from '../../OverviewSection';
import { PlacesOfRelevanceSection } from '../../places';
import { NexgoExperiencesSection } from '../../experiences';
import { ResponseChat } from '../../ResponseChat';
import { FeedbackContainer } from '../../feedback';
import { QuestionsContainer } from '../../questions';
import { useMediaQuery } from '@/hooks/useMediaQuery';

export interface DiscoverTabContentProps {
  isActive: boolean;
  assistantMessage: ChatMessage;
  overviewDescription: string;
  experiences: ExperienceCard[];
  onExperienceClick?: (experienceId: string) => void;
  locations: ChatLocation[]; // All valid locations (tourist attractions and businesses)
  onCardClick?: (cardId: string) => void;
  onPlaceNameClick?: (placeName: string) => void;
  peopleAlsoAsk: PeopleAlsoAskType[];
  onPeopleAlsoAskClick?: (itemId: string, question: string) => void;
  onLike?: (messageId: string) => void;
  onDislike?: (messageId: string) => void;
  onCopy?: (messageId?: string, content?: string) => void;
  onShare?: (messageId?: string, content?: string) => void;
  onDownload?: (messageId?: string, content?: string) => void;
  getFeedback: (messageId: string) => 'like' | 'dislike' | null;
}

export const DiscoverTabContent: React.FC<DiscoverTabContentProps> = ({
  isActive,
  assistantMessage,
  overviewDescription,
  experiences,
  onExperienceClick,
  locations,
  onCardClick,
  onPlaceNameClick,
  peopleAlsoAsk,
  onPeopleAlsoAskClick,
  onLike,
  onDislike,
  onCopy,
  onShare,
  onDownload,
  getFeedback,
}) => {
  const [showQuestions, setShowQuestions] = useState(true);
  // Detect mobile view (less than 768px)
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Manage collapsed state for sections - collapse when switching to mobile, expand when switching to desktop
  const [experiencesCollapsed, setExperiencesCollapsed] = useState(false);
  const [placesCollapsed, setPlacesCollapsed] = useState(false);

  // Collapse sections when switching to mobile, expand when switching back to desktop
  useEffect(() => {
    if (isMobile) {
      setExperiencesCollapsed(true);
      setPlacesCollapsed(true);
    } else {
      setExperiencesCollapsed(false);
      setPlacesCollapsed(false);
    }
  }, [isMobile]);

  const overview = {
    title: 'Quick Overview',
    description:
      assistantMessage.overview ||
      assistantMessage.content.substring(0, 300) +
        (assistantMessage.content.length > 300 ? '...' : '') ||
      overviewDescription,
  };

  return (
    <div
      style={{
        display: isActive ? 'flex' : 'none', // Hide instead of unmounting
        flexDirection: 'column',
        gap: spacing[4],
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Quick Overview */}
      <OverviewSection
        title={overview.title}
        description={overview.description}
      />

      {/* Divider after Overview */}
      <div
        style={{
          width: '100%',
          height: '1px',
          backgroundColor: strokeColors.base,
        }}
      />

      {/* Places of Relevance - Collapsed when switching to mobile - Show all locations (tourist attractions and businesses) */}
      {locations && locations.length > 0 && (
        <>
          <PlacesOfRelevanceSection
            title='Places of Relevance'
            locations={locations}
            onCardClick={onCardClick}
            isCollapsed={placesCollapsed}
            onToggle={setPlacesCollapsed}
          />
          {/* Divider after Places of Relevance */}
          <div
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: strokeColors.base,
            }}
          />
        </>
      )}

      {/* Response Chat */}
      <ResponseChat
        messages={[assistantMessage]}
        onPlaceNameClick={onPlaceNameClick}
      />

      {/* Nexgo Experiences - Collapsed when switching to mobile */}
      {experiences && experiences.length > 0 && (
        <>
          <NexgoExperiencesSection
            experiences={experiences}
            onCardClick={onExperienceClick}
            isCollapsed={experiencesCollapsed}
            onToggle={setExperiencesCollapsed}
          />
          {/* Divider after Nexgo Experiences */}
          <div
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: strokeColors.base,
            }}
          />
        </>
      )}

      {/* Feedback Container - Per response */}
      <FeedbackContainer
        messageId={assistantMessage.id}
        onLike={() => onLike?.(assistantMessage.id)}
        onDislike={() => onDislike?.(assistantMessage.id)}
        onCopy={() => onCopy?.(assistantMessage.id, assistantMessage.content)}
        onShare={() => onShare?.(assistantMessage.id, assistantMessage.content)}
        onBookmark={() =>
          onDownload?.(assistantMessage.id, assistantMessage.content)
        }
        liked={getFeedback(assistantMessage.id) === 'like'}
        disliked={getFeedback(assistantMessage.id) === 'dislike'}
      />

      {/* Questions - Per response */}
      {showQuestions && (
        <QuestionsContainer
          questions={peopleAlsoAsk.map(item => item.question)}
          onQuestionClick={question => {
            // Find the item by question and call the original handler
            const item = peopleAlsoAsk.find(i => i.question === question);
            if (item) {
              onPeopleAlsoAskClick?.(item.id, item.question);
            }
          }}
          onClose={() => setShowQuestions(false)}
        />
      )}
    </div>
  );
};
